import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { fecha, fechaHora, gramos } from "../lib/formato";
import { Cargando } from "../componentes/piezas";
import { useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

interface SocioLibro {
  numeroSocio: number;
  nombre: string;
  apellidos: string;
  documentoTipo: string;
  documentoNumero: string;
  fechaNacimiento: string;
  fechaAlta: string;
  fechaBaja: string | null;
  motivoBaja: string | null;
  estado: string;
  consentimientoRgpd: boolean;
  fechaConsentimiento: string | null;
  consumoPrevistoMensualMg: number;
  avalista: { numeroSocio: number } | null;
}

interface Libro {
  club: {
    nombre: string;
    cif: string | null;
    direccion: string | null;
    ciudad: string | null;
    provincia: string | null;
    cp: string | null;
    telefono: string | null;
    email: string | null;
  };
  emitidoEn: string;
  emitidoPor: string;
  socios: SocioLibro[];
  resumen: {
    total: number;
    activos: number;
    suspendidos: number;
    bajas: number;
    pendientes: number;
    conConsentimiento: number;
    consumoDeclaradoMensualMg: number;
  };
}

/**
 * En un documento oficial, "14.772 g" se presta a leerse como catorce gramos y
 * pico. En kilos no hay duda.
 */
function kilos(miligramos: number): string {
  const kg = miligramos / 1_000_000;
  return kg >= 1
    ? `${kg.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg`
    : gramos(miligramos);
}

/**
 * Libro de socios, compuesto para imprimir o guardar como PDF.
 *
 * Es el registro que una asociación esta obligada a llevar, y el papel que se
 * enseña si alguien lo pide. Se compone aquí y no en el servidor porque el
 * navegador ya sabe imprimir y guardar en PDF: meter una librería de PDF en el
 * servidor sería peso para hacer lo mismo peor.
 *
 * Salen también las bajas: un libro del que desaparecen los que se fueron no
 * vale como libro.
 */
export default function LibroSocios() {
  const { t, cuenta } = useIdioma();
  const libro = useQuery({ queryKey: ["libro-socios"], queryFn: () => api<Libro>("/exportar/libro-socios") });

  if (libro.isLoading || !libro.data) return <Cargando />;
  const { club, socios, resumen } = libro.data;

  const direccion = [club.direccion, [club.cp, club.ciudad].filter(Boolean).join(" "), club.provincia]
    .filter(Boolean)
    .join(" · ");

  return (
    <div>
      <div className="sin-imprimir fila separada" style={{ marginBottom: 16 }}>
        <Link to="/socios" className="boton secundario">
          ← {t("lb.volver")}
        </Link>
        <button className="boton" onClick={() => window.print()}>
          {t("lb.imprimir")}
        </button>
      </div>

      <p className="sin-imprimir suave pequeno">
        {t("lb.comoPdf")}
      </p>

      <article className="documento">
        <header>
          <h1>{t("lb.titulo")}</h1>
          <h2>{club.nombre}</h2>
          <p className="datos-club">
            {/* Se juntan los que hay y se separan después: montando el
                separador junto a cada dato, un club sin CIF empezaba su
                libro por un punto suelto. */}
            {[club.cif && `CIF ${club.cif}`, direccion, club.telefono && `Tel. ${club.telefono}`, club.email]
              .filter(Boolean)
              .join(" · ")}
          </p>
          <p className="emision">
            {t("lb.emision", { fecha: fechaHora(libro.data.emitidoEn), quien: libro.data.emitidoPor })}
          </p>
        </header>

        <table>
          <thead>
            <tr>
              <th>{t("lb.numero")}</th>
              <th>{t("lb.apellidosNombre")}</th>
              <th>{t("lb.documento")}</th>
              <th>{t("lb.nacimiento")}</th>
              <th>{t("lb.alta")}</th>
              <th>{t("lb.baja")}</th>
              <th>{t("lb.avalista")}</th>
              <th>{t("lb.rgpd")}</th>
              <th>{t("lb.estado")}</th>
            </tr>
          </thead>
          <tbody>
            {socios.map((s) => (
              <tr key={s.numeroSocio} className={s.estado === "BAJA" ? "baja" : undefined}>
                <td className="num">{s.numeroSocio}</td>
                <td>
                  {s.apellidos}, {s.nombre}
                </td>
                <td>
                  {s.documentoTipo} {s.documentoNumero}
                </td>
                <td>{fecha(s.fechaNacimiento)}</td>
                <td>{fecha(s.fechaAlta)}</td>
                <td>{s.fechaBaja ? fecha(s.fechaBaja) : "—"}</td>
                <td className="num">
                  {s.avalista ? t("lb.numeroAvalista", { numero: s.avalista.numeroSocio }) : "—"}
                </td>
                <td>
                  {s.consentimientoRgpd
                    ? s.fechaConsentimiento
                      ? fecha(s.fechaConsentimiento)
                      : t("lb.si")
                    : "—"}
                </td>
                <td>{t(`g.estado.${s.estado}` as Clave)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {socios.length === 0 && <p className="vacio-doc">{t("lb.sinSocios")}</p>}

        <footer>
          <p>
            <strong>{cuenta(resumen.total, "lb.unInscrito", "lb.inscritos")}</strong>{" "}
            {t("lb.enTotal", {
              reparto: [
                t("lb.enAlta", { n: resumen.activos }),
                resumen.suspendidos > 0 && t("lb.suspendidos", { n: resumen.suspendidos }),
                resumen.pendientes > 0 && t("lb.pendientes", { n: resumen.pendientes }),
                t("lb.bajas", { n: resumen.bajas }),
              ]
                .filter(Boolean)
                .join(", "),
            })}{" "}
            {t("lb.consentimiento", {
              conConsentimiento: resumen.conConsentimiento,
              total: resumen.total,
            })}
          </p>
          <p>
            {t("lb.consumoDeclarado", {
              alMes: gramos(resumen.consumoDeclaradoMensualMg),
              alAnio: kilos(resumen.consumoDeclaradoMensualMg * 12),
            })}
          </p>
          <p className="nota">{t("lb.notaPie")}</p>
        </footer>
      </article>
    </div>
  );
}
